import {
  getDecisionNodeAtPath,
  getCurrentShopifyOrderFromSubscription,
  extractMacroSnippets,
  getSmbOfSelectedTicket,
  getDiscountDescription,
  getSelectedSubscriptionForConfirmationPage,
  getRbiApprovedRefundAmount,
} from '../extractFromState';
import { formatDate, parseUTCDate } from '../util';
import {
  getFirstSelectedSubscription,
  getRefundAmount,
  getSelectedOrderNumber,
  getSelectedOrderNames,
  getSelectedSubscriptionItemNames,
  getlastOrderInSelectedSubscription,
  getSelectedOrder,
  getAmazonOrderWarrantyTotalYears,
  getCurrencyCode,
  getBillingZipCodeOfSelectedOrder,
  getEmailOfSelectedOrder,
  getSelectedOrderName,
  getShippingAddressOfSelectedOrder,
  getOrderingSystem,
  getRefundAmountFormatted,
  getPauseSubscriptionDetails,
} from './tools';
import { DECISION_NODE_ACTIONS } from '../constants/decisionTree';
import regex from 'constants/regex';

const _templateMap = {
  currentShopifyOrderFromSubscription: getCurrentShopifyOrderFromSubscription,
  selectedSubscription: getFirstSelectedSubscription, // for now let's assume that all the selected subscriptions have the same scheduled charge date
  selectedSubscriptionNames: getSelectedSubscriptionItemNames,
  selectedSMBName: getSmbOfSelectedTicket,
  refundAmount: getRefundAmount,
  refundAmountFormatted: getRefundAmountFormatted,
  selectedOrderNumber: getSelectedOrderNumber,
  selectedOrderNames: getSelectedOrderNames,
  discountDescription: getDiscountDescription,
  selectedSubscriptionForConfirmationPage: getSelectedSubscriptionForConfirmationPage,
  lastOrderInSelectedSubscription: getlastOrderInSelectedSubscription,
  selectedOrder: getSelectedOrder,
  amazonOrderWarrantyTotalYears: getAmazonOrderWarrantyTotalYears,
  currencyCode: getCurrencyCode,
  billingZipcode: getBillingZipCodeOfSelectedOrder,
  selectedOrderEmail: getEmailOfSelectedOrder,
  selectedOrderName: getSelectedOrderName,
  rbiApprovedRefundAmount: getRbiApprovedRefundAmount,
  orderShippingAddress: getShippingAddressOfSelectedOrder,
  pauseSubscription: getPauseSubscriptionDetails,
};

// fields to format in simple mm/dd/yyyy
const dateFieldsToFormat = [
  'currentShopifyOrderFromSubscription.shipped_date',
  'selectedSubscription.next_charge_scheduled_at',
  'selectedOrder.PurchaseDate',
];

// reads from state
export const replaceTemplatedText = (state, text) => {
  if (!text) {
    return '';
  }

  const orderingSystem = getOrderingSystem(state);

  return text.replace(regex.copyTextSelectorRegex, (match, path) => {
    const splitPath = path.split('.');
    const selectorFn = _templateMap[splitPath[0]];

    // if we have a selector for this path, use it
    if (selectorFn) {
      const value = selectorFn(state);

      // rbi refund amount is in cents, need to convert to dollars
      if (splitPath[0] === 'rbiApprovedRefundAmount' && !isNaN(value)) {
        return (value / 100).toFixed(2);
      }

      if (splitPath[0] === 'orderShippingAddress') {
        return generateOrderShippingAddressForTemplate(value, orderingSystem);
      }

      // if path then follow path
      if (splitPath.length > 1) {
        const out = value && value[splitPath[1]];
        if (out) {
          // see if this is a date we need to format
          if (dateFieldsToFormat.indexOf(path) > -1) {
            return formatDate(parseUTCDate(out));
          }

          return out;
        }
      } else {
        // else return the value returned by the selector
        return value;
      }
    }

    // default to the template
    return match;
  });
};

// does not read from state, instead params passed
export const replaceTemplatedTextForMultipleItems = (text, paramsArray) => {
  if (!text) {
    return '';
  }

  return text.replace(
    regex.copyTextSelectorRegex,
    (match, path) => paramsArray.map((param) => param[path]).join(' and ') || match
  );
};

export const getTemplatedCopyTextFromCurrentDecisionNode = (state) => {
  const decisionNodeActionsWithSnippets = [
    DECISION_NODE_ACTIONS.SEND_CONSTRUCTED_MACRO,
    DECISION_NODE_ACTIONS.SEND_CONSTRUCTED_MACRO_AND_PARTIAL_REFUND_ITEMS,
    DECISION_NODE_ACTIONS.CANCEL_AND_REFUND_ORDER_ITEMS_THEN_SEND_MACRO,
    DECISION_NODE_ACTIONS.CANCEL_AND_REFUND_OR_DISCOUNT_ORDER_ITEMS_THEN_SEND_MACRO,
  ];
  const decisionNode = getDecisionNodeAtPath(state);
  if (decisionNodeActionsWithSnippets.includes(decisionNode.action)) {
    const snippetsArr = [...extractMacroSnippets(state)];
    const showGreetingsAndClosing = decisionNode.hasOwnProperty('onlyUseSnippet') ? !decisionNode.onlyUseSnippet : true;

    if (showGreetingsAndClosing) {
      snippetsArr.unshift(decisionNode.greeting);
      snippetsArr.push(decisionNode.closing);
    }

    return snippetsArr.join('\n\n');
  }

  return getTemplatedTextFromCurrentDecisionNode(state, 'copyText');
};

export const getTemplatedTextFromCurrentDecisionNode = (state, fieldName) => {
  const decisionNode = getDecisionNodeAtPath(state);
  if (!decisionNode) {
    return null;
  }
  let text = replaceTemplatedText(state, decisionNode[fieldName]);

  return text;
};

export const getQuestionFromCurrentDecisionNode = (state) => {
  const decisionNode = getDecisionNodeAtPath(state);
  if (!decisionNode) {
    return null;
  }

  return decisionNode.question;
};

function generateOrderShippingAddressForTemplate(shippingAddress, orderingSystem) {
  if (orderingSystem === 'shopify') {
    return `\n\nCustomer: ${shippingAddress.first_name} ${shippingAddress.last_name}\nAddress line 1: ${shippingAddress.address1}\nAddress line 2: ${shippingAddress.address2}\nCity: ${shippingAddress.city}\nState/Region: ${shippingAddress.province}\nPostal Code: ${shippingAddress.zip}\nCountry: ${shippingAddress.country}\nPhone: ${shippingAddress.phone}`;
  }

  return `\n\nCustomer: ${shippingAddress.firstName} ${shippingAddress.lastName}\nAddress line 1: ${shippingAddress.address1}\nAddress line 2: ${shippingAddress.address2}\nCity: ${shippingAddress.city}\nState/Region: ${shippingAddress.region}\nPostal Code: ${shippingAddress.zip}\nCountry: ${shippingAddress.country}\nPhone: ${shippingAddress.phone}`;
}
